import { useEffect, useState } from "react"

import { isPresentationReceiver } from "../cast/castConfig"
import type { CastRoomMessage } from "../cast/castConfig"
import type { PresentationConnection } from "../cast/presentationTypes"

/**
 * The display side of the Presentation API.
 *
 * The controller launches `/tv/<CODE>?receiver=1`, so in the ordinary case the
 * room is already in the URL and this hook has nothing to add — the page is
 * watching before any message arrives. It exists for the cases where that isn't
 * true: a presentation reconnected to, one launched from Chrome's own cast
 * button before a room was known, or a controller that later points the screen
 * somewhere else.
 *
 * The spec allows several controllers on one presentation, so every connection
 * in the list is listened to, not just the first.
 */

export function usePresentationReceiverRoom(): string | null {
    const [code, setCode] = useState<string | null>(null)

    useEffect(() => {
        if (!isPresentationReceiver()) return

        let cancelled = false
        const listen = (connection: PresentationConnection) => {
            connection.onmessage = event => {
                if (cancelled) return
                const parsed = parseRoomMessage(event.data)
                if (parsed) setCode(parsed)
            }
        }

        navigator.presentation?.receiver?.connectionList
            .then(list => {
                if (cancelled) return
                list.connections.forEach(listen)
                list.onconnectionavailable = event => listen(event.connection)
            })
            .catch(error => console.error("Presentation: no connection list", error))

        return () => { cancelled = true }
    }, [])

    return code
}

/** Accept either a JSON string or an already-parsed object — controllers differ,
 *  and this is the one point at which a screen learns what to show. */
export function parseRoomMessage(data: unknown): string | null {
    let payload: unknown = data
    if (typeof data === "string") {
        try { payload = JSON.parse(data) } catch { return null }
    }
    if (!payload || typeof payload !== "object") return null
    const raw = (payload as Partial<CastRoomMessage>).roomCode
    if (typeof raw !== "string") return null
    const code = raw.toUpperCase().replace(/[^A-Z0-9]/g, "")
    return code || null
}
